/**
 * Building Regulations Approved Document G3 & MCS MIS 3005 Plumbing and Renewables Calculations
 */

/**
 * G3 Discharge Pipe Sizing (Table G3 Guidance)
 * Minimum size of discharge pipe D2 from tundish based on valve outlet size D1 and equivalent resistance length
 */
export const G3_DISCHARGE_SIZING_TABLE = [
  {
    valveOutletD1: 'G 1/2',
    minD2Size: 22,
    maxEquivLength: 9,
    resistancePerElbow: 0.8,
    resistancePerBend: 0.5
  },
  {
    valveOutletD1: 'G 1/2',
    minD2Size: 28,
    maxEquivLength: 18,
    resistancePerElbow: 1.0,
    resistancePerBend: 0.6
  },
  {
    valveOutletD1: 'G 1/2',
    minD2Size: 35,
    maxEquivLength: 27,
    resistancePerElbow: 1.4,
    resistancePerBend: 0.9
  },
  {
    valveOutletD1: 'G 3/4',
    minD2Size: 28,
    maxEquivLength: 9,
    resistancePerElbow: 1.0,
    resistancePerBend: 0.6
  },
  {
    valveOutletD1: 'G 3/4',
    minD2Size: 35,
    maxEquivLength: 18,
    resistancePerElbow: 1.4,
    resistancePerBend: 0.9
  },
  {
    valveOutletD1: 'G 3/4',
    minD2Size: 42,
    maxEquivLength: 27,
    resistancePerElbow: 1.7,
    resistancePerBend: 1.1
  },
  {
    valveOutletD1: 'G 1',
    minD2Size: 35,
    maxEquivLength: 9,
    resistancePerElbow: 1.4,
    resistancePerBend: 0.9
  },
  {
    valveOutletD1: 'G 1',
    minD2Size: 42,
    maxEquivLength: 18,
    resistancePerElbow: 1.7,
    resistancePerBend: 1.1
  },
  {
    valveOutletD1: 'G 1',
    minD2Size: 54,
    maxEquivLength: 27,
    resistancePerElbow: 2.3,
    resistancePerBend: 1.5
  }
];

/**
 * Calculate equivalent pipe length for G3 D2 discharge pipework
 * @param {number} straightLengthMeters - Length of straight pipe in meters
 * @param {number} elbowsCount - Number of 90-degree elbows
 * @param {number} bendsCount - Number of formed slow bends
 * @param {number} pipeDiameterMm - Diameter in mm (22, 28, 35, 42, 54)
 * @returns {number} Total equivalent resistance length in meters
 */
export const calculateEquivalentDischargeLength = (
  straightLengthMeters = 0,
  elbowsCount = 0,
  bendsCount = 0,
  pipeDiameterMm = 22
) => {
  const match = G3_DISCHARGE_SIZING_TABLE.find(item => item.minD2Size === Number(pipeDiameterMm)) || G3_DISCHARGE_SIZING_TABLE[0];
  const elbowResistance = match.resistancePerElbow * (Number(elbowsCount) || 0);
  const bendResistance = match.resistancePerBend * (Number(bendsCount) || 0);
  return Number((Number(straightLengthMeters || 0) + elbowResistance + bendResistance).toFixed(2));
};

/**
 * Validate G3 Discharge pipe installation parameters
 * @param {Object} params
 * @param {string} params.valveOutletD1 - 'G 1/2', 'G 3/4', or 'G 1'
 * @param {number} params.pipeDiameterMm - 22, 28, 35, 42, 54
 * @param {number} params.equivalentLength - Total equivalent length in meters
 * @param {number} params.fallPerMeterMm - Gradient fall in mm per meter (min 1:200 = 5mm/m, recommended 1:100 = 10mm/m)
 * @param {number} params.tundishDropMm - Vertical drop from valve to tundish (max 600mm)
 * @returns {Object} validation result { compliant, maxAllowedLength, issues: [] }
 */
export const validateG3Discharge = ({
  valveOutletD1 = 'G 1/2',
  pipeDiameterMm = 22,
  equivalentLength = 0,
  fallPerMeterMm = 10,
  tundishDropMm = 500
}) => {
  const issues = [];
  const matches = G3_DISCHARGE_SIZING_TABLE.filter(
    item => item.valveOutletD1 === valveOutletD1 && item.minD2Size === Number(pipeDiameterMm)
  );

  if (matches.length === 0) {
    issues.push(`Selected D2 diameter (${pipeDiameterMm}mm) is undersized for ${valveOutletD1} valve outlet. Must be at least one size larger.`);
  }

  const maxAllowed = matches.length > 0 ? matches[0].maxEquivLength : 0;
  if (equivalentLength > maxAllowed && maxAllowed > 0) {
    issues.push(`Equivalent length of ${equivalentLength}m exceeds maximum permissible ${maxAllowed}m for ${pipeDiameterMm}mm pipe.`);
  }

  if (Number(fallPerMeterMm) < 5) {
    issues.push(`Discharge fall of ${fallPerMeterMm}mm/m is below statutory minimum gradient of 1:200 (5mm/m).`);
  }

  if (Number(tundishDropMm) > 600) {
    issues.push(`Vertical drop to tundish (${tundishDropMm}mm) exceeds maximum allowable 600mm.`);
  }

  return {
    compliant: issues.length === 0,
    maxAllowedLength: maxAllowed,
    issues
  };
};

/**
 * Heat Pump Design Flow Rate Calculation (MCS MIS 3005)
 * Formula: Flow Rate (L/min) = (Design Heat Loss in kW * 60) / (Specific Heat Capacity * Delta T)
 * Where Water Cp ≈ 4.187 kJ/kg·K and 25-30% Glycol Cp ≈ 3.98 kJ/kg·K
 * @param {number} designHeatLossKw - Design heat loss in kW
 * @param {number} deltaT - Temperature differential between flow and return (°C), typically 5°C
 * @param {number} glycolConcentrationPct - Antifreeze percentage (0 - 40%)
 * @returns {Object} { flowRateLpm, flowRateM3h, heatLossKw, deltaT, requiredPipeVelocityMs }
 */
export const calculateHeatPumpFlowRate = (
  designHeatLossKw = 8,
  deltaT = 5,
  glycolConcentrationPct = 25
) => {
  const kw = Number(designHeatLossKw) || 0;
  const dt = Number(deltaT) || 5;
  const glycol = Number(glycolConcentrationPct) || 0;

  // Specific heat capacity adjusted for glycol (water = 4.187, 25% propylene glycol ≈ 3.98, 40% ≈ 3.75)
  const specificHeat = 4.187 - (glycol * 0.008); // kJ/kg·K
  const density = 1.0 + (glycol * 0.0015); // kg/L

  if (kw <= 0 || dt <= 0) {
    return { flowRateLpm: 0, flowRateM3h: 0, heatLossKw: kw, deltaT: dt };
  }

  // kW = (L/s * density * specificHeat * dt)
  // L/s = kW / (density * specificHeat * dt)
  const flowRateLps = kw / (density * specificHeat * dt);
  const flowRateLpm = Number((flowRateLps * 60).toFixed(2));
  const flowRateM3h = Number(((flowRateLpm * 60) / 1000).toFixed(2));

  return {
    flowRateLpm,
    flowRateM3h,
    heatLossKw: kw,
    deltaT: dt,
    glycolConcentrationPct: glycol,
    specificHeat: Number(specificHeat.toFixed(3))
  };
};

/**
 * Glycol Freeze Protection Lookup Table (Propylene Glycol)
 */
export const GLYCOL_FREEZE_PROTECTION = [
  { concentration: 10, freezePointC: -3, refractiveIndexBrix: 6.0, phMin: 7.5, phMax: 9.5 },
  { concentration: 15, freezePointC: -5, refractiveIndexBrix: 8.5, phMin: 7.5, phMax: 9.5 },
  { concentration: 20, freezePointC: -8, refractiveIndexBrix: 11.0, phMin: 7.5, phMax: 9.5 },
  { concentration: 25, freezePointC: -11, refractiveIndexBrix: 14.0, phMin: 7.5, phMax: 9.5 },
  { concentration: 30, freezePointC: -15, refractiveIndexBrix: 17.0, phMin: 7.5, phMax: 9.5 },
  { concentration: 35, freezePointC: -19, refractiveIndexBrix: 20.0, phMin: 7.5, phMax: 9.5 },
  { concentration: 40, freezePointC: -24, refractiveIndexBrix: 23.0, phMin: 7.5, phMax: 9.5 }
];

/**
 * Lookup freeze protection based on measured refractometer Brix reading or glycol percentage
 * @param {number} percentageOrBrix - Percentage (0-50) or Refractometer Brix
 * @param {boolean} isBrix - Whether value provided is Brix scale
 * @returns {Object} { freezePointC, recommendedMinPct, status }
 */
export const evaluateGlycolProtection = (value = 25, isBrix = false) => {
  const num = Number(value) || 0;
  let matched = GLYCOL_FREEZE_PROTECTION[3]; // default 25%

  if (isBrix) {
    matched = GLYCOL_FREEZE_PROTECTION.reduce((prev, curr) =>
      Math.abs(curr.refractiveIndexBrix - num) < Math.abs(prev.refractiveIndexBrix - num) ? curr : prev
    );
  } else {
    matched = GLYCOL_FREEZE_PROTECTION.reduce((prev, curr) =>
      Math.abs(curr.concentration - num) < Math.abs(prev.concentration - num) ? curr : prev
    );
  }

  const isSatisfactory = matched.freezePointC <= -10; // UK MCS minimum recommendation is -10°C to -15°C
  return {
    ...matched,
    isSatisfactory,
    status: isSatisfactory ? 'PASS' : 'WARNING_LOW_CONCENTRATION',
    recommendation: isSatisfactory
      ? `Protection down to ${matched.freezePointC}°C is compliant with MCS guidelines.`
      : `Freeze protection (${matched.freezePointC}°C) is inadequate. Boost concentration to at least 25% (-11°C to -15°C).`
  };
};

/**
 * TMV Fail-safe Temperature Matrix & Tolerances (TMV2 & TMV3 / NHS D08)
 */
export const TMV_APPLICATION_LIMITS = {
  tmv2_washbasin: { maxTempC: 41, name: 'Washbasin (TMV2)', standard: 'BS EN 1287 / BS EN 1111' },
  tmv2_shower: { maxTempC: 41, name: 'Shower (TMV2)', standard: 'BS EN 1287 / BS EN 1111' },
  tmv2_bath_unassisted: { maxTempC: 46, name: 'Bath (Unassisted Fill TMV2)', standard: 'BS EN 1287 / BS EN 1111' },
  tmv3_bidet: { maxTempC: 38, name: 'Bidet (TMV3 / Healthcare)', standard: 'NHS D08' },
  tmv3_washbasin: { maxTempC: 41, name: 'Washbasin (TMV3 / Healthcare)', standard: 'NHS D08' },
  tmv3_shower: { maxTempC: 41, name: 'Shower (TMV3 / Healthcare)', standard: 'NHS D08' },
  tmv3_bath_assisted: { maxTempC: 44, name: 'Bath (Assisted Fill TMV3 / Healthcare)', standard: 'NHS D08' }
};

/**
 * Validate TMV test result
 * @param {string} applicationKey - Key from TMV_APPLICATION_LIMITS
 * @param {number} mixedTempC - Measured mixed water temperature °C
 * @param {number} failSafeShutOffSec - Time to isolate mixed outlet upon cold failure (seconds)
 * @returns {Object} { pass: boolean, issues: [] }
 */
export const validateTMVTest = (applicationKey, mixedTempC, failSafeShutOffSec) => {
  const config = TMV_APPLICATION_LIMITS[applicationKey] || TMV_APPLICATION_LIMITS.tmv2_shower;
  const issues = [];
  const temp = Number(mixedTempC) || 0;
  const shutOffTime = Number(failSafeShutOffSec) || 0;

  if (temp > config.maxTempC) {
    issues.push(`Mixed water temperature of ${temp}°C exceeds maximum permitted ${config.maxTempC}°C for ${config.name}.`);
  }

  if (shutOffTime > 1.5) {
    issues.push(`Fail-safe cold water isolation response time of ${shutOffTime}s exceeds standard maximum of 1.5 seconds.`);
  }

  return {
    pass: issues.length === 0,
    maxPermittedTempC: config.maxTempC,
    application: config.name,
    issues
  };
};
